import React, { useState, useEffect } from 'react';
import { Panel } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import { useRequestData } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import Facet from './Facet';
import useFetchFacets from '../hooks/useFetchFacets';
import { transformUserRangeData } from '../utilities/facet-user-range';
import { MODAL } from '../constant';
import RangeForm from './RangeForm/RangeForm';
import Icon from './Icon';

const FacetGroup = (props) => {
	const { facet, index, selectedValues } = props;
	const [filters, setFilter] = useState(null);
	const { type, multiselect, id: facetId, values: facetGroup, label } = facet;
	const facetType = multiselect ? 'checkbox' : 'radio';
	const [open, setOpen] = useState(false);

	const { deviceType, windowId, widgetName } = useRequestData();
	const isDesktop = deviceType === 'DESKTOP';
	const isUserRange = type === 'USER_RANGE';
	const [userSelections, setUserSelections] = useState(selectedValues);

	const createMarkup = () => {
		return { __html: `${label}` };
	};

	useFetchFacets(filters, MODAL);

	useEffect(() => {
		setUserSelections(selectedValues);
	}, [selectedValues]);

	useEffect(() => {
		setOpen(userSelections.length > 0);
	}, [userSelections]);

	if (isUserRange) {
		const [facetValues] = transformUserRangeData(facet, !!multiselect);

		// Hide facet group when we have no values to draw histogram chart
		if (!facetValues) {
			return null;
		}
	}

	const handleChange = (event) => {
		const targetValue = event.target.value;
		let newSelectedIds = [];
		if (facetType === 'checkbox') {
			const { checked } = event.target;
			newSelectedIds = selectedValues.map((selection) => selection.value);

			if (checked) {
				newSelectedIds = [...newSelectedIds, targetValue];
			} else {
				newSelectedIds = newSelectedIds.filter(
					(item) => item !== targetValue
				);
			}
			// Don't push anything to updatedSelections when the selection is ALL
		} else if (targetValue !== 'ALL') {
			newSelectedIds = [...newSelectedIds, targetValue];
		}

		setUserSelections(
			facetGroup.filter((selection) =>
				newSelectedIds.includes(selection.value)
			)
		);

		if (newSelectedIds.includes(targetValue)) {
			trackEvent(`${widgetName}-modal`, windowId, {
				action: 'applied',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet added'
			});
		} else {
			trackEvent(`${widgetName}-modal`, windowId, {
				action: 'removed',
				element: `${facetId} selection`,
				elementCTA: targetValue,
				fieldValue: targetValue,
				result: 'Results updated with facet removed'
			});
		}

		setFilter({ [facetId]: newSelectedIds });
	};

	const facetCondition = () => {
		if (type === 'USER_RANGE') {
			const [facetValues, noPriceObj] = transformUserRangeData(
				facet,
				!!multiselect
			);

			return (
				<RangeForm
					facetValues={facetValues}
					noPriceObj={noPriceObj}
					facetId={facetId}
					isMultiselect={!!multiselect}
					selectedValues={selectedValues}
				/>
			);
		}

		return facetGroup.map((item, facetIndex) => {
			let selected = !!userSelections.find(
				(selection) => selection.value === item.value
			);
			if (userSelections.length === 0) {
				// If item.selected is true facet item is set to selected.
				selected = item.value === 'ALL' ? true : !!item.selected;
			}
			return (
				<Facet
					key={item.value}
					index={`facet-${facetIndex}`}
					type={facetType}
					itemData={item}
					isSelected={selected}
					onChange={handleChange}
					facetId={facetId}
					isDesktop={isDesktop}
				/>
			);
		});
	};

	const handleCollapseClick = () => {
		const nextToggleState = open ? 'collapsed' : 'expanded';
		trackEvent(`${widgetName}-modal`, windowId, {
			action: nextToggleState,
			element: `${facetId} accordion`,
			result: `Accordion ${nextToggleState}`
		});

		setOpen(!open);
	};

	if (facetGroup.length === 0) {
		return null;
	}

	return (
		<div className="facet-list">
			<Panel
				className="facet-list-group"
				role="tablist"
				id={index}
				onToggle={debounce(handleCollapseClick, 100)}
				expanded={open}
				data-facet-group={index}
			>
				<Panel.Heading className="text-link w-100 text-decoration-none panel-heading">
					<Panel.Title className="panel-title" toggle>
						<span className="w-100 d-flex justify-content-between">
							<span
								className="facet-list-group-label"
								// eslint-disable-next-line react/no-danger
								dangerouslySetInnerHTML={createMarkup()}
							/>
							<Icon
								name={open ? 'collapse' : 'expand'}
								className="icon-style-collapse"
							/>
						</span>
					</Panel.Title>
				</Panel.Heading>
				<Panel.Body collapsible>
					<ul
						className={`
								list-unstyled line-height-condensed ${
									isDesktop ? 'row mb-0' : ''
								} align-items-center d-flex align-items-center flex-wrap
							`}
					>
						{facetCondition()}
					</ul>
				</Panel.Body>
			</Panel>
		</div>
	);
};

FacetGroup.propTypes = {
	facet: PropTypes.shape({
		type: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		id: PropTypes.string.isRequired,
		isCurrency: PropTypes.bool,
		multiselect: PropTypes.bool,
		values: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
		collapsed: PropTypes.bool.isRequired
	}).isRequired,
	selectedValues: PropTypes.arrayOf(
		PropTypes.shape({
			label: PropTypes.string.isRequired,
			value: PropTypes.string.isRequired
		}).isRequired
	),
	index: PropTypes.string.isRequired
};

export default FacetGroup;
